import { l } from '@adornis/base/rxjs-extension/rxjs-helpers.js';
import { type Maybe } from '@adornis/base/utilTypes.js';
import { getWorkshopAppointmentParticipationConfirmationLink } from '@adornis/buchungsheld-db/api/participation.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-loading-state.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import * as QRCodeLibrary from 'qrcode';
import { filter, switchMap } from 'rxjs';

@customElement('bh-prompt-workshop-qr')
export class BHPromptWorkshopQR extends TrDialog<void> {
  static override get element_name() {
    return 'bh-prompt-workshop-qr';
  }

  @property({ attribute: false }) workshopID = new RXController<Maybe<string>>(this, undefined);

  @state() private readonly confirmationLinks = new RXController(
    this,
    this.workshopID.observable.pipe(
      filter(Boolean),
      switchMap(workshopID => {
        return getWorkshopAppointmentParticipationConfirmationLink(workshopID)({
          validFrom: 1,
          validTill: 1,
          url: 1,
          appointment: { name: 1 },
        });
      }),
      switchMap(async links =>
        Promise.all(
          links.map(async (link, i) => {
            const qrCode = await QRCodeLibrary.toDataURL(link.url);
            return {
              validFrom: link.validFrom,
              validTill: link.validTill,
              url: link.url,
              qrCode,
              appointmentName: link.appointment.name ?? 'Termin ' + (i + 1),
            };
          }),
        ),
      ),
      l('_confirmationLinks'),
    ),
  );

  override render() {
    if (this.confirmationLinks.isLoading || !this.confirmationLinks.value)
      return html` <x-loading-state></x-loading-state> `;

    const items = this.confirmationLinks.value.map(link => {
      return html`
        <x-flex space="xs" crossaxis-center>
          <!-- hadding -->
          <x-flex horizontal space="md" crossaxis-center center space-between>
            <x-h3> ${link.appointmentName} </x-h3>
            <tr-button-small
              .color=${this.colors.secondary.shade(300)}
              icon="content_copy"
              .action=${() => this.copyLink(link.url)}
            >
              <x-text>${this._translation.translate('copy')}</x-text>
            </tr-button-small>
          </x-flex>

          <!-- qrcode -->
          <img src="${link.qrCode}" alt="QR Code" />

          <!-- still valid ? -->
          ${(() => {
            if (link.validFrom)
              return html`
                <x-flex vertical>
                  <x-text>
                    ${this._translation.translate('PROMPT', 'qr_valid_from')}:
                    ${link.validFrom.toFormat('dd.LL.yyyy HH:mm')}
                  </x-text>
                  <br />
                  ${link.validTill
                    ? html`
                        <x-text>
                          ${this._translation.translate('PROMPT', 'qr_valid_till')}:
                          ${link.validTill.toFormat('dd.LL.yyyy HH:mm')}
                        </x-text>
                      `
                    : nothing}
                </x-flex>
              `;

            if (!link.validTill) return nothing;

            const remaining = link.validTill.diff(DateTime.now()).toFormat('HH:mm:ss');
            if (link.validTill < DateTime.now())
              return html` <x-text flex> ${this._translation.translate('PROMPT', 'qr_no_longer_valid')} </x-text> `;
            return html`
              <x-text flex> gültig bis ${link.validTill.toFormat('dd.LL.yyyy HH:mm')} (${remaining}) </x-text>
            `;
          })()}
        </x-flex>
        <x-hr></x-hr>
      `;
    });

    return html` <x-flex space="md"> ${items} </x-flex> `;
  }

  copyLink(link: string) {
    navigator.clipboard.writeText(link).then(() => XSnackbar.show(this._translation.translate('copied')));
  }

  override async cancel(returnVal?: any): Promise<void> {
    await this.close();
  }
}
